import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './CategoryPage.css';



const CategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const enableLocalApi = process.env.REACT_APP_ENABLE_LOCAL_API === 'true';
  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
  const API_URL = envApiUrl || '/api';
  const useStaticCatalogOnly = process.env.NODE_ENV === 'development'
    && isLocalHost
    && !!envApiUrl
    && envApiUrl.includes('localhost')
    && !enableLocalApi;
  const fetchConfig = useMemo(() => ({
    apiUrl: API_URL,
    useStaticCatalogOnly,
    slug
  }), [API_URL, useStaticCatalogOnly, slug]);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const brandFilter = useMemo(() => queryParams.get('brand'), [queryParams]);

  useEffect(() => {
    const fetchCategoryAndProducts = async () => {
      const otherProductCatId = queryParams.get('otherProductCategory');

      if (otherProductCatId) {
        try {
          setLoading(true);
          setError('');

          const [otherRes, prodRes] = await Promise.all([
            fetch(`${fetchConfig.apiUrl}/other-products`),
            fetch(`${fetchConfig.apiUrl}/products?otherProductCategory=${otherProductCatId}&all=true`)
          ]);

          const otherData = await otherRes.json();
          const prodData = await prodRes.json();

          if (otherData.success && Array.isArray(otherData.data)) {
            const matchedCat = otherData.data.find(op => op._id === otherProductCatId);
            if (matchedCat) {
              setCategory({
                name: matchedCat.name,
                description: matchedCat.description || ''
              });
            } else {
              setCategory({
                name: 'Category',
                description: ''
              });
            }
          }

          if (prodData.success && Array.isArray(prodData.data)) {
            const filtered = prodData.data.filter(p => {
              const pOtherId = p.otherProductCategory?._id || p.otherProductCategory;
              return pOtherId === otherProductCatId;
            });
            setProducts(filtered);
          }
        } catch (err) {
          console.error('Error fetching otherProductCategory data:', err);
          setError('Failed to connect to server.');
          setProducts([]);
        } finally {
          setLoading(false);
        }
        return;
      }

      if (fetchConfig.useStaticCatalogOnly) {
        setError('');
        setProducts([]);
        setCategory({
          name: fetchConfig.slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          description: ''
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        setProducts([]);

        // Fetch category
        const catRes = await fetch(`${fetchConfig.apiUrl}/categories/${fetchConfig.slug}`, {
          headers: {
          }
        });
        const catData = await catRes.json();

        // Fetch products in this category
        const prodRes = await fetch(`${fetchConfig.apiUrl}/products?categorySlug=${fetchConfig.slug}&limit=1000`, {
          headers: {
          }
        });
        const prodData = await prodRes.json();

        if (catData.success) {
          setCategory(catData.data);
        } else {
          setCategory({
            name: fetchConfig.slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            description: ''
          });
        }

        if (prodRes.ok && prodData.success) {
          const nextProducts = Array.isArray(prodData.data) ? prodData.data : [];
          setProducts(nextProducts);
        }
      } catch (err) {
        console.error('Error fetching category data:', err);
        setError('Failed to connect to server.');
        setProducts([]);
        setCategory({
          name: fetchConfig.slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
          description: ''
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryAndProducts();
  }, [fetchConfig, queryParams, t]);

  const filteredProducts = useMemo(() => {
    if (!brandFilter || queryParams.get('otherProductCategory')) return products;
    const brandLower = brandFilter.toLowerCase();
    return products.filter(p => {
      const nameLower = (p.name || '').toLowerCase();
      const matchesName = nameLower.includes(brandLower);

      // Check brand from otherProductCategory populate
      const opBrandLower = p.otherProductCategory?.brand?.toLowerCase();
      const matchesCategoryBrand = opBrandLower === brandLower;

      if (brandLower === 'other') {
        return !nameLower.includes('kisan') && !nameLower.includes('bcs') && !nameLower.includes('shaktiman') && !opBrandLower;
      }
      return matchesName || matchesCategoryBrand;
    });
  }, [products, brandFilter, queryParams]);

  const categoryDisplayName = useMemo(() => {
    const baseName = category ? category.name : slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    if (!brandFilter) return baseName;
    const brandDisplay = brandFilter.toLowerCase() === 'other' ? 'Other' : brandFilter.toUpperCase();
    return `${brandDisplay} ${baseName}`;
  }, [category, slug, brandFilter]);

const NO_IMAGE_PLACEHOLDER = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSczMDAnIGhlaWdodD0nMzAwJyB2aWV3Qm94PScwIDAgMzAwIDMwMCc+PHJlY3Qgd2lkdGg9JzEwMCUnIGhlaWdodD0nMTAwJScgZmlsbD0nI2YxZjVmOScvPjx0ZXh0IHg9JzUwJScgeT0nNTAlJyBkb21pbmFudC1iYXNlbGluZT0nbWlkZGxlJyB0ZXh0LWFuY2hvcj0nbWlkZGxlJyBmb250LWZhbWlseT0nc2Fucy1zZXJpZicgZm9udC1zaXplPScxNicgZmlsbD0nIzk0YTNiOCc+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+";

  const getImage = (imgName) => {
    if (!imgName) return NO_IMAGE_PLACEHOLDER;
    if (imgName.startsWith('http://') || imgName.startsWith('https://') || imgName.startsWith('data:')) {
      if (imgName.includes('ik.imagekit.io')) {
        const hasParams = imgName.includes('?');
        return imgName + (hasParams ? '&' : '?') + 'tr=w-500,q-80,f-auto';
      }
      return imgName;
    }
    
    const cleanImgName = (imgName.startsWith('/uploads/') || imgName.startsWith('uploads/')) ? imgName : `/uploads/${imgName}`;
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.') || window.location.hostname.startsWith('172.') || window.location.hostname.startsWith('10.') || window.location.port !== '';
    let backendBase = isLocalHost ? 'http://localhost:5000' : fetchConfig.apiUrl.replace('/api', '');
    if (!backendBase || backendBase.startsWith('/')) {
      backendBase = `${window.location.protocol}//${window.location.hostname}:5000`;
    }
    return `${backendBase}${cleanImgName.startsWith('/') ? '' : '/'}${cleanImgName}`;
  };

  return (
    <div className="category-page">
      <Header />
      
      <main className="category-main-content">
        <div className="container">
          <div className="category-header-clean">
            <button className="category-back-btn" onClick={() => navigate('/products')} aria-label="Go Back">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
            <h1 className="cat-clean-title">{categoryDisplayName}</h1>
            <p className="cat-clean-subtitle">{category?.description || ''}</p>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--primary-color)' }}>{t('category_page.loading_prod')}</div>
          ) : error && filteredProducts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--accent-color)' }}>{error}</div>
          ) : filteredProducts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'gray' }}>{t('category_page.no_prod')}</div>
          ) : (
            <div className="category-products-grid-clean">
              {filteredProducts.map((prod, i) => (
                <div key={prod._id || i} className="clean-product-card">
                  <div className="clean-img-wrap" onClick={() => prod.slug ? navigate(`/product/${prod.slug}`) : null} style={{ cursor: prod.slug ? 'pointer' : 'default' }}>
                    <img 
                      src={getImage(prod.images && prod.images[0], prod.name)} 
                      alt={prod.name} 
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = NO_IMAGE_PLACEHOLDER;
                      }}
                    />
                  </div>
                  <div className="clean-info">
                    <h3 className="clean-title">{prod.name}</h3>
                    <p className="clean-desc">{prod.description}</p>
                    <div className="clean-actions">
                      {prod.slug ? (
                        <button className="btn-view-details-clean" onClick={() => navigate(`/product/${prod.slug}`)}>{t('category_page.view_details')}</button>
                      ) : (
                        <div style={{ flex: 1 }}></div>
                      )}
                      <button className="btn-enquiry-clean" onClick={() => window.open('https://wa.me/919631277264', '_blank')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CategoryPage;


